import type { BeckyModel } from '@wix/thunderbolt-becky-root'
import { removeHash } from '@wix/thunderbolt-becky-root'
import type { RefSchemas, TimeAnimationOptions } from '@wix/thunderbolt-becky-types'
import type {
	CompVariantsViewItem,
	MegaStoreWithSubscriptions } from '@wix/thunderbolt-catharsis'
import {
	CatharsisMegaStoreSymbol,
	ComponentsCssStringifierSymbol
} from '@wix/thunderbolt-catharsis'
import type { IComponentCssStringifier, BreakpointsVariantsTo } from '@wix/thunderbolt-catharsis-extensions'
import type { CatharsisConstants } from '@wix/thunderbolt-catharsis-ioc'
import { CatharsisSingleTransactionSymbol } from '@wix/thunderbolt-catharsis-ioc'
import { getCSSAnimation } from '@wix/motion'
import { Container } from '@wix/thunderbolt-ioc'
import { commonLoader } from './common'
import { getModuleLoader } from './loaders/css'
import type { ModuleLoaderFactory } from './types'

type BreakpointToEffectMap = BreakpointsVariantsTo<TimeAnimationOptions | null>

function getPresets(documentData: any, pageId: string, requestedPageId?: string): Array<string> {
	const widgetDescriptors = Object.values(documentData) as Array<{
		type: string
		rootCompId: string
		presets: Array<string>
	}>

	const widgetDescriptor = widgetDescriptors.find(
		(dataItem) =>
			(dataItem.type === 'WidgetDescriptor' && dataItem.rootCompId === '#' + pageId) ||
			dataItem.rootCompId === '#' + requestedPageId
	)
	return widgetDescriptor!.presets.map((presetRef) => {
		const { presetId } = documentData[presetRef.slice(1)]
		return removeHash(presetId)!
	})
}

const getCatharsisConstants = (beckyModel: BeckyModel, isEditor: boolean, schemas: RefSchemas): CatharsisConstants => {
	return {
		isExperimentOpen: (exp) => !!beckyModel.experiments[exp],
		isResponsiveMasterPage: !isEditor && beckyModel.isResponsive && beckyModel.pageCompId === 'masterPage',
		isEditor,
		schemas,
		experiments: beckyModel.experiments,
		componentsMappers: beckyModel.componentsMappers,
		isBuilderComponentModel: beckyModel.isBuilderComponentModel,
	}
}

const fontsCollector = (megaStore: MegaStoreWithSubscriptions) => {
	const fontsCss: Set<string> = new Set()
	megaStore.getChildStore('fontFacesCss').subscribe((__, fontFacesCss: Array<string>) => {
		fontFacesCss.forEach((fontFaceCss) => fontFacesCss && fontsCss.add(fontFaceCss))
	})
	return fontsCss
}

function getOffset(offset: number | undefined, count: number, index: number) {
	return typeof offset === 'number' ? offset : index / (count - 1)
}

function keyframePropertyToCSS(key: string): string {
	if (key === 'cssFloat') {
		return 'float'
	}
	if (key === 'easing') {
		return 'animation-timing-function'
	}
	return key.replace(/([A-Z])/g, (match) => `-${match.toLowerCase()}`)
}

function generateKeyframesCss(keyframes: Array<Record<string, string | number | undefined>>): string {
	const count = keyframes.length

	return keyframes
		.map(({ offset, ...keyframe }, index) => {
			const declaration = Object.entries(keyframe)
				.filter(([key, value]) => key !== 'offset' && value !== undefined && value !== null)
				.map(([key, value]) => `${keyframePropertyToCSS(key)}: ${value};`)
				.join(' ')

			return `${getOffset(offset as number, count, index) * 100}% { ${declaration} }`
		})
		.join('\n')
}

const keyframesController = (megaStore: MegaStoreWithSubscriptions) => {
	const renderedNames: Set<string> = new Set()
	const keyframesCss: Set<string> = new Set()

	megaStore
		.getChildStore('motionTimeAnimations')
		.subscribe((__, animations: Array<{ data: BreakpointToEffectMap; variants: CompVariantsViewItem }> | null) => {
			animations?.forEach((animation) => {
				if (animation.data) {
					Object.values(animation.data).forEach((variation) => {
						Object.values(variation).forEach((animationOptions) => {
							const effectName = animationOptions?.namedEffect?.type

							if (effectName && !renderedNames.has(effectName)) {
								renderedNames.add(effectName)
								const effects = getCSSAnimation('stub', animationOptions)

								effects.forEach((effect) => {
									if (effect.keyframes && effect.keyframes.length > 0) {
										const keyframesName = effect.name || effectName

										if (!renderedNames.has(keyframesName)) {
											renderedNames.add(keyframesName)
											const keyframes = generateKeyframesCss(effect.keyframes)

											keyframesCss.add(`@keyframes ${keyframesName} { ${keyframes} }`)
										}
									}
								})
							}
						})
					})
				}
			})
		})

	return keyframesCss
}

// This method is used for public flow and for testing the editor flow
export const getCatharsisCssInternal = (loaderFactory: ModuleLoaderFactory, isEditor: boolean) => (
	beckyModel: BeckyModel,
	schemas: RefSchemas,
	runAndReport: <T>(fn: () => T, name: string) => T
) => {
	// Create the container and load the modules
	const container = Container()
	const catharsisConstants = getCatharsisConstants(beckyModel, isEditor, schemas)
	const featuresLoader = loaderFactory(catharsisConstants)
	container.load(featuresLoader, commonLoader)

	// Get the megaStore and singleTransaction from the container
	const megaStore = container.get(CatharsisMegaStoreSymbol)
	const singleTransaction = container.get(CatharsisSingleTransactionSymbol)
	const {
		getComponentsCssData,
		stringifyComponentsCssData,
		generatePresetsCss,
	}: IComponentCssStringifier = container.get(ComponentsCssStringifierSymbol)

	const fontsCss = fontsCollector(megaStore as MegaStoreWithSubscriptions)

	const keyframesCss = keyframesController(megaStore as MegaStoreWithSubscriptions)

	// Execute the singleTransaction
	runAndReport(() => singleTransaction.execute(beckyModel, runAndReport), 'singleTransaction')

	// resolve all components css
	const { viewMode, pageCompId, shouldSplitByPresets } = beckyModel
	const allCompsCss = runAndReport(() => {
		const css = runAndReport(() => getComponentsCssData(pageCompId, viewMode === 'mobile'), 'getComponentsCssData')

		if (shouldSplitByPresets) {
			const presets = getPresets(beckyModel.data.document_data, pageCompId, beckyModel.requestedPageId)
			const presetsToComponentsCss = runAndReport(() => generatePresetsCss(css, presets), 'generatePresetsCss')
			const joinedFonts = Array.from(fontsCss).join('\n')
			const joinedKeyframes = Array.from(keyframesCss).join('\n')
			presetsToComponentsCss.default = [joinedFonts, joinedKeyframes, presetsToComponentsCss.default].join('\n')
			return { css, cssResult: presetsToComponentsCss }
		}

		const cssString = runAndReport(() => stringifyComponentsCssData(css), 'stringifyComponentsCssData')
		return { css, cssResult: cssString }
	}, 'stringifyAllComponentsCss')

	const cssResult = shouldSplitByPresets
		? allCompsCss.cssResult
		: runAndReport(
			() =>
				[
					/* catharsis font faces */
					...fontsCss,
					/* catharsis keyframes */
					...keyframesCss,
					/* catharsis components css */
					allCompsCss.cssResult,
				].join('\n'),
			'concat all css'
		)

	return { megaStore, fontsCss, keyframesCss, css: allCompsCss.css, cssResult }
}

export const getViewerCatharsisCss = getCatharsisCssInternal(getModuleLoader, false)
